import { multiWindow, openMessageWindow, updates } from "@transport";
import type { ComponentType } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import * as composeWindow from "../lib/composeWindow";
import * as connection from "../lib/connection";
import * as filters from "../lib/filters";
import { addressList, displayName, hueFor, initials, plural, shortDate } from "../lib/format";
import { escapeHtml } from "../lib/html";
import * as nav from "../lib/navigation";
import { colourVar } from "../lib/palette";
import * as priority from "../lib/priority";
import * as reminders from "../lib/reminders";
import { openInNewWindow, type SwipeAction } from "../lib/settings";
import * as store from "../lib/store";
import * as swipe from "../lib/swipe";
import * as tabs from "../lib/tabs";
import * as updateWatch from "../lib/update-watch";
import type { Envelope } from "../lib/types";
import { SelectionBar } from "./Actions";
import { FilterBar, FilterMenu, scopeLabel } from "./Filters";
import * as Icon from "./Icons";

export function MessageList() {
  const rows = store.rows.value;
  const scope = store.scope.value;
  const sentinel = useRef<HTMLDivElement>(null);
  // Captured so the null check below narrows inside the click handler as well
  // as in the JSX: `updates` is a module binding, and TypeScript will not carry
  // a narrowing on one of those across a closure.
  const updater = updates;

  // Paging by observer rather than by scroll handler: it fires once when the
  // foot comes into view instead of on every frame of a flick.
  useEffect(() => {
    const node = sentinel.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void store.loadMore();
      },
      { rootMargin: "600px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [rows.length]);

  // Only ever lower-cased into the search placeholder, which is why a role gets
  // away with being the raw word. The two cross-folder views take their names
  // from the store so this and the sidebar cannot drift apart.
  const title =
    scope.kind === "unified"
      ? scope.role
      : scope.kind === "starred"
        ? store.STARRED_LABEL
        : scope.kind === "saved"
          ? store.KEPT_LABEL
          : scope.kind === "reminders"
            ? store.REMINDERS_LABEL
          : (store.mailboxes.value.get(scope.accountId)?.find((m) => m.id === scope.mailboxId)
              ?.name ?? "mailbox");

  return (
    <section class="col list-col" data-pane-name="list" tabIndex={-1} aria-label="Messages">
      {/*
        SPIKE, step 0 of the titlebar phase. `deep` makes the whole row a drag
        region except its clickable children -- Tauri's own script treats
        INPUT, BUTTON, A, SELECT, TEXTAREA and anything with a tabindex as
        blocking, so the search box and the three buttons keep their clicks
        without being marked up.

        Inert in a browser: nothing there reads the attribute.
      */}
      <div class="col-head" data-tauri-drag-region="deep">
        {/*
          The way to the mailboxes on a layout that has no room to keep them on
          screen. Rendered only there -- a hamburger next to a sidebar that is
          already visible is a control that does nothing.
        */}
        <button
          class="icon-btn pane-open"
          aria-label="Mailboxes and accounts"
          title="Mailboxes and accounts"
          onClick={() => (nav.folderNav.value = true)}
        >
          <Icon.Menu />
        </button>
        <div class="search">
          <Icon.Search />
          <input
            type="search"
            placeholder={`Search ${title.toLowerCase()}…`}
            value={store.search.value}
            aria-label="Search messages"
            onInput={(e) => store.setSearch((e.target as HTMLInputElement).value)}
          />
        </div>
        {/*
          Beside the search box, because narrowing a list by typing and
          narrowing it by ticking a box are the same intent and used to be two
          rows apart. Before Refresh rather than after: Refresh is the only
          control here that is not about *which* messages, and putting it last
          keeps the two that are together.
        */}
        <FilterMenu />
        <button
          class="icon-btn"
          title="Refresh"
          aria-label="Refresh"
          onClick={() => void store.refresh()}
        >
          {store.loading.value ? <span class="spinner" /> : <Icon.Refresh />}
        </button>
      </div>

      {/*
        Above the filter bar, because it is the more permanent of the two: the
        tabs say *where you are* and the filter bar says what you have
        temporarily narrowed that to. The other order would put a control that
        is usually absent above one that is always present, and the list would
        appear to move down the screen whenever a filter went on.
      */}
      {/*
        Said plainly, because a list of saved mail is indistinguishable from a
        list of current mail and the difference is the whole question somebody
        is asking when they wonder whether a reply has arrived. Driven by what
        the last answer *was* rather than by `navigator.onLine`, which lies in
        one direction, or by the event stream, which can be down while requests
        still succeed. It clears itself the moment the daemon answers again.
      */}
      {connection.showingSaved.value && (
        <div class="banner">
          <Icon.Warning />
          <span>Offline — showing mail saved on this device. It may not be up to date.</span>
        </div>
      )}
      {/*
        The background check's one visible surface (lib/update-watch.ts). It
        earns a place this prominent because the client runs for days: the
        Software card nobody opens is where this news used to go to die.
        Install stays a button — the check found it, the reader decides.
      */}
      {updater !== null && updateWatch.showing() && updateWatch.found.value !== null && (
        <div class="banner">
          <Icon.Refresh />
          {updateWatch.installing.value !== null ? (
            /*
              The silence this closes: the artifact is around a hundred
              megabytes, so between the click and the restart there was a long
              wait during which the banner still read "ready to install" and
              both buttons still invited a second press. The percentage is the
              updater's own count of bytes, which this app was already being
              told and was discarding -- see `src-tauri/src/update.rs`.
            */
            <span style={{ flex: 1 }}>{updateWatch.describe(updateWatch.installing.value)}</span>
          ) : (
            <>
              <span style={{ flex: 1 }}>
                HydraMail {updateWatch.found.value.version} is ready to install.
              </span>
              <button
                class="btn"
                onClick={() =>
                  void updateWatch.install(updater).catch(() => {
                    /* The Software card reports install failures with room to
                       explain; a banner cannot. Nothing to do here: the app is
                       still the old one, `installing` has been cleared, and the
                       banner is back offering another try. */
                  })
                }
              >
                Restart &amp; install
              </button>
              <button class="btn" onClick={() => updateWatch.snooze()}>
                Later
              </button>
            </>
          )}
        </div>
      )}
      <InboxTabs />

      {/*
        Above the selection bar and outside `col-body`, so it is on screen
        whatever the list is doing: scrolled, empty, still loading, or covered
        by a bulk-action bar. An active filter that can be scrolled past is an
        active filter someone will miss.
      */}
      <FilterBar />

      {store.selectionCount.value > 0 && <SelectionBar />}

      <div class="col-body">
        {[...store.partialErrors.value].map(([accountId, error]) => (
          <div class="banner error" key={accountId}>
            <Icon.Warning />
            <span>
              {store.accounts.value.find((a) => a.id === accountId)?.name ?? accountId}: {error}
            </span>
          </div>
        ))}

        {store.loading.value && rows.length === 0 && <Skeletons />}

        {!store.loading.value &&
          rows.length === 0 &&
          // No accounts is a different situation from an empty inbox, and
          // saying "nothing here, wait for the next sync" when the daemon has
          // nothing to sync sends someone waiting for mail that will never
          // arrive. There is no Inbox in the sidebar for the same reason.
          (store.session.value && store.accounts.value.length === 0 && !store.listError.value ? (
            <NoAccounts />
          ) : store.listError.value ? (
            <div class="empty">
              <Icon.Inbox />
              <h2>Could not load messages</h2>
              <p>{store.listError.value}</p>
            </div>
          ) : filters.anyActive.value ? (
            <NothingMatches />
          ) : store.activeBucket.value === "low" ? (
            // An empty low-priority tab is a good outcome rather than a
            // problem, and it is also the tab a first-time reader is most
            // likely to open out of curiosity. Saying where the contents would
            // come from turns "why is this empty" into "ah, I have not
            // demoted anyone yet".
            <div class="empty">
              <Icon.Inbox />
              <h2>Nothing low priority</h2>
              <p>
                Mail from senders you moved here. It never raises a notification.
              </p>
            </div>
          ) : scope.kind === "reminders" && !store.search.value ? (
            <div class="empty">
              <Icon.Bell />
              <h2>No reminders</h2>
              <p>Set one from a message's menu: Remind me.</p>
            </div>
          ) : scope.kind === "starred" && !store.search.value ? (
            // "New mail shows up here after the next sync" is false in both of
            // the views below, and falsely reassuring: nothing arrives in
            // either of them by itself, so somebody told to wait would wait for
            // ever. Each says instead what would put a message here.
            <div class="empty">
              <Icon.Star />
              <h2>Nothing starred</h2>
              <p>
                Starred messages from every folder and account.
              </p>
            </div>
          ) : scope.kind === "saved" && !store.search.value ? (
            <div class="empty">
              <Icon.KeepOffline />
              <h2>Nothing saved</h2>
              <p>
                Saved messages stay readable offline.
              </p>
            </div>
          ) : (
            <div class="empty">
              <Icon.Inbox />
              <h2>Nothing here</h2>
              <p>
                {store.search.value
                  ? "No message matches that search."
                  : "New mail shows up here after the next sync."}
              </p>
            </div>
          ))}

        {/*
          A list of rows, not a listbox. Each row now holds two controls -- a
          checkbox and the row itself -- and `role="option"` may not contain
          interactive children, so keeping the old roles would have described a
          widget that is not what is on screen. The tick is what carries
          selection to a screen reader, and it says so itself.
        */}
        <div class="list" role="list" aria-label="Messages">
          {rows.map((row) => (
            <Row key={store.keyOf(row)} row={row} />
          ))}
        </div>

        <div ref={sentinel} class="list-foot">
          {store.loadingMore.value ? (
            <>
              <span class="spinner" /> Loading more…
            </>
          ) : rows.length > 0 && store.allExhausted() ? (
            // "that's everything" is a claim about the whole mailbox when
            // nothing is filtered and a claim about the filter when something
            // is, and saying the first while the second is true is how a foot
            // note starts arguing with the sentence above the list.
            //
            // It counts matches rather than rows, because a filtered list can
            // hold a row that no longer matches -- the one the reader just
            // marked read. Counting that row here would make the foot disagree
            // with the sentence at the top of the same column, over a
            // difference the row itself is already labelled with.
            filters.anyActive.value ? (
              `${plural(store.matchTotal.value ?? rows.length, "message")} — that's everything matching`
            ) : (
              `${plural(rows.length, "message")} — that's everything`
            )
          ) : null}
        </div>
      </div>
    </section>
  );
}

/**
 * Two tabs over one inbox.
 *
 * # Why this is not a sidebar entry
 *
 * Because nothing moved. Low priority is a judgement this daemon keeps about
 * senders; on the provider every one of these messages is in the inbox, with
 * the same flags it always had. A row in the folder list beside Archive and
 * Junk would claim a folder exists, and the first person to open Thunderbird
 * and fail to find it would be right to conclude the app had lied to them.
 * Tabs say what is true: one mailbox, two ways of looking at it.
 *
 * # The counts
 *
 * Unread, per tab, and they are the reason the split earns its keep. The
 * focused count is the mailbox's unread minus the low-priority share -- see
 * `store.unreadTotal` -- so demoting a newsletter visibly brings the number
 * down. The other tab still shows its own, quietly and never in red, because
 * "low priority" has to mean "not interrupting you" rather than "hidden from
 * you"; a bucket whose contents cannot be seen to exist is a bucket that eats
 * mail.
 *
 * A zero is drawn as nothing at all rather than as "0", on both tabs. Nobody
 * needs telling how many messages they have not read when the answer is none.
 */
function InboxTabs() {
  if (!store.hasBuckets.value) return null;
  const counts = store.bucketCounts.value;
  const current = store.activeBucket.value;

  const tab = (id: priority.Bucket, label: string, count: number | undefined) => (
    <button
      role="tab"
      aria-selected={current === id}
      class={`inbox-tab${current === id ? " is-current" : ""}`}
      onClick={() => store.setBucket(id)}
    >
      <span>{label}</span>
      {/*
        Unread and nothing else. A total was tried in this slot and taken back
        out: it meant both tabs always carried a number, and a number that is
        always there stops being read -- which costs the unread count the only
        job it has, which is to be noticed.

        The count is inside the button and part of its accessible name, so a
        screen reader reads "Low priority, 12 unread, tab" as one thing. A
        separate live region would announce it again on every refresh.
      */}
      {count ? (
        <span class="tab-count" aria-label={`${count} unread`}>
          {count}
        </span>
      ) : null}
    </button>
  );

  return (
    <div class="inbox-tabs" role="tablist" aria-label="Inbox">
      {tab("focused", "Focused", counts?.focused)}
      {tab("low", "Low priority", counts?.low)}
    </div>
  );
}

/**
 * A filter that matched nothing.
 *
 * Separate from "Nothing here" because they are opposite situations that look
 * identical: one means the mailbox is empty, the other means the mailbox is
 * full of mail the reader has told this list not to show them. Saying "Nothing
 * here" for the second is the bug this whole feature has to avoid -- it is the
 * sentence that sends someone off to check whether the daemon has stopped
 * syncing.
 *
 * So it names the filters, names the mailbox, and offers the way out. It is
 * also the one place a search and a filter are both worth mentioning, because
 * either could be the reason and only the reader knows which they meant.
 */
function NothingMatches() {
  const f = filters.filters.value;
  const text = store.search.value.trim();
  const held = store.scopeTotal.value;
  return (
    <div class="empty">
      <Icon.Filter />
      <h2>
        No {filters.messagesPhrase(f)} in {scopeLabel()}
      </h2>
      <p>
        {/*
          "The filters are hiding your mail" is the reassuring sentence and it
          is also a claim, so it is only made where it is true. An empty Trash
          with a filter on gets told the truth instead: there is nothing here
          either way. Where the count is known it is said out loud, because
          "78 messages here, all filtered out" settles the question in a way
          that no amount of reassurance does.
        */}
        {text
          ? `Nothing matches “${text}” and the filters together.`
          : held === 0
            ? "There is no mail here at all, filters or not."
            : held === undefined
              ? "The mail is still there — this is the filters, not a failed sync."
              : `${plural(held, "message")} here, all of them filtered out.`}
      </p>
      <button class="btn" style={{ marginTop: 12 }} onClick={() => store.clearFilters()}>
        Clear filters
      </button>
    </div>
  );
}

/**
 * The state a fresh install actually lands in: a valid session, a working
 * daemon, and nothing to read because no mail account exists yet.
 *
 * It used to print a `hydramaild account add-imap` line, from the days when the
 * CLI was the only way to do this. The Accounts screen adds an account over
 * HTTP now, so the shell command was a second, worse route sitting above the
 * button that does it -- and a recipe someone would follow instead of pressing
 * the thing next to it.
 */
function NoAccounts() {
  return (
    <div class="empty">
      <Icon.Users />
      <h2>No mail accounts yet</h2>
      <p>The daemon is answering; there is nothing for it to collect. Add an account to start.</p>
      <button class="btn" style={{ marginTop: 12 }} onClick={() => (store.view.value = "accounts")}>
        Open Accounts
      </button>
    </div>
  );
}

/**
 * How long a touch has to stay put before it means "start selecting".
 *
 * 500ms is Android's own `ViewConfiguration` long-press timeout, so the app
 * agrees with every other list on the phone rather than inventing a duration.
 * The slop is what separates a press from the beginning of a scroll: a finger
 * resting on a row drifts a few pixels, and cancelling on the first movement
 * would make the gesture impossible to perform.
 */
const HOLD_MS = 500;
const HOLD_SLOP = 10;

function Row({ row }: { row: Envelope }) {
  const { email, accountId } = row;
  const key = store.keyOf(row);
  const isOpen = store.openEmailId.value === key;
  const checked = store.selection.value.has(key);
  const selecting = store.selectionCount.value > 0;
  const flagged = Boolean(email.keywords["$flagged"]);
  const snippet = store.snippets.value.get(key);
  // Held in a filtered list although it no longer matches, because the reader
  // is the one who made it stop matching. See `store.reprieved`.
  const stale = store.reprieved.value.has(key);
  const reminder = reminders.forMessage(accountId, email.id);
  const multiAccount = store.accounts.value.length > 1;
  const accountName = store.accounts.value.find((a) => a.id === accountId)?.name;
  // Classified low priority and yet on screen in the focused tab: the star won.
  // Gated on `hasBuckets` rather than on `activeBucket`, which reads "focused"
  // in every view that has no tabs at all -- so this used to fire in a search,
  // where there is no tab for the message to have escaped from and nothing
  // surprising about it being on screen.
  const lowPriorityButShown = Boolean(email.lowPriority) && store.hasBuckets.value && flagged;
  // Low priority in a view with no tabs: a search, or Archive, or Sent. Nothing
  // is hidden there by design, so without a word on the row a demoted sender's
  // mail is indistinguishable from everything else and the reader has no way to
  // tell whether a rule they wrote is doing anything. Quiet, because in a search
  // this can be most of the results and it is a fact about the row rather than
  // something to answer.
  const lowPriorityHere = Boolean(email.lowPriority) && !store.hasBuckets.value;

  /*
    Whose name goes on the row, decided per message and not per view.

    A Sent list built the ordinary way is a column of the reader's own name
    beside the reader's own avatar, over and over, which is indistinguishable
    from a folder of mail they received from themselves. In a unified Sent view
    it is worse: every row is from one of their *several* identities, so the
    only thing telling the rows apart is the account colour stripe.

    The question is asked of `mailboxIds` rather than of `scope`, because scope
    only knows the role for a unified view -- so one account's own Sent folder
    was still showing the sender -- and knows nothing at all during a search,
    which can return outgoing and incoming mail in the same result set. Per
    message is the only version that is right in all three.
  */
  const role = store.roleOf(accountId, email);
  const outgoing = role === "sent" || role === "drafts";
  const sender = email.from?.[0];
  // Bcc-only mail is a real thing in a Sent folder, so `cc` is a fallback
  // rather than a curiosity. Two names and a count: three wrap on a phone.
  const recipients = email.to?.length ? email.to : email.cc;
  const counterpart = outgoing ? recipients?.[0] : sender;
  const who = outgoing
    ? addressList(recipients, 2) || (role === "drafts" ? "(no recipient yet)" : "(no recipient)")
    : displayName(sender) || "(unknown sender)";

  /*
    The avatar follows the same address.

    It used to be seeded from the sender unconditionally while the *initials*
    already followed the recipient, so an outgoing row drew someone else's
    letters in the reader's own colour. Half-converted is worse than either
    whole answer: the colour is what the eye groups by when scanning, so the
    rows still read as one correspondent.
  */
  const hue = hueFor(counterpart?.email ?? "?");

  /*
    Unread emphasis is suppressed on outgoing rows.

    A message in Sent is unread when some other client filed it with IMAP
    APPEND and did not set \Seen -- a fact about that client, not about
    anything the reader has to deal with. Drawing those rows bold makes a Sent
    folder look like a queue of things needing attention, which is the other
    half of why this view read as an inbox. The keyword itself is untouched:
    the Unread filter still finds them, and the bar above the list says so in
    words when it does.
  */
  const unread = !email.keywords["$seen"] && !outgoing;

  /*
    `sentAt` on an outgoing row, because that is the date a person means by
    "when did I send this". `receivedAt` on a Sent copy is when it was filed,
    which for an imported or re-synced archive is the same instant for every
    message in the folder and tells the reader nothing.

    The list is still *sorted* by `receivedAt` -- that is what the query does
    and what the merge cutoff depends on -- so the two can in principle
    disagree about order. In practice a client appends the copy as it sends,
    and in the case where they diverge badly it is the import case, where the
    sort key is the meaningless one anyway.
  */
  const when = (outgoing ? email.sentAt : null) ?? email.receivedAt;

  const describe = `${email.subject || "(no subject)"} ${outgoing ? "to" : "from"} ${who}`;

  /*
    The account's colour, as a stripe down the left edge -- but only in a list
    that is actually mixing accounts, and only as a `--row-acct` custom property
    the stylesheet decides what to do with. The open row's accent border has to
    keep beating it, and an inline `borderLeftColor` would beat every stylesheet
    rule instead. The stripe is the fastest of these six to read and the least
    intrusive; the account's name is a few pixels below it in `row-tags`, which
    is what makes it a shortcut rather than the only way to tell.
  */
  const accountColour = multiAccount && accountName ? store.colourOf(accountId) : undefined;

  /*
   * Long press to start selecting, because on a phone the tick is not drawn
   * until something is selected -- see the rule in `styles.css`. This is the
   * gesture that space was reclaimed for.
   *
   * Touch only. A mouse keeps the always-drawn tick and modifier-click, and
   * making a slow mouse click select would punish a hesitant hand for nothing.
   */
  const holdTimer = useRef<number | null>(null);
  const held = useRef(false);
  const origin = useRef({ x: 0, y: 0 });

  const cancelHold = () => {
    if (holdTimer.current === null) return;
    clearTimeout(holdTimer.current);
    holdTimer.current = null;
  };

  // A row that scrolls away mid-press would otherwise select itself after it
  // had gone, from a timer nothing was left to cancel.
  useEffect(() => cancelHold, []);

  const beginHold = (e: PointerEvent) => {
    if (e.pointerType === "mouse") return;
    // Cleared here rather than only on the click that follows: a press that
    // fires and then slides off the row never produces one, and a stale flag
    // would silently swallow the next tap.
    held.current = false;
    origin.current = { x: e.clientX, y: e.clientY };
    holdTimer.current = window.setTimeout(() => {
      holdTimer.current = null;
      held.current = true;
      store.toggleSelect(row);
    }, HOLD_MS);
  };

  const maybeCancelHold = (e: PointerEvent) => {
    if (holdTimer.current === null) return;
    if (
      Math.abs(e.clientX - origin.current.x) > HOLD_SLOP ||
      Math.abs(e.clientY - origin.current.y) > HOLD_SLOP
    ) {
      cancelHold();
    }
  };

  /*
   * Drag the row sideways to act on it. The half that decides what a drag means
   * is `lib/swipe.ts`; this is the half that touches a pointer and an element,
   * and it is deliberately as small as that split can make it.
   *
   * Three refs and one piece of state. The state is the drawn gesture, because
   * the row has to re-render to move; the refs are what a re-render must not
   * disturb -- where the finger started, how wide the row was then, and which
   * bindings were in force. Those are read once at `pointerdown` rather than
   * per move, so a setting changed in another tab mid-drag cannot swap the
   * action out from under the label the person is looking at.
   *
   * No `setPointerCapture`. A touch pointer is implicitly captured by the
   * element it went down on for the life of the gesture, so the moves and the
   * release arrive here even when the finger has left the row -- which it will,
   * because the row is being dragged out from under it.
   */
  const drag = useRef<{
    x: number;
    y: number;
    width: number;
    left: SwipeAction;
    right: SwipeAction;
  } | null>(null);
  const swiped = useRef(false);
  const [gesture, setGesture] = useState<swipe.Gesture | null>(null);

  const beginSwipe = (e: PointerEvent) => {
    // Cleared before any of the guards below, and that ordering is the point:
    // the release of an armed swipe suppresses its own click, so the flag is
    // often still set when the next press arrives and nothing else is
    // guaranteed to run in between. Clearing it after a guard had returned
    // would leave a press that is not a swipe -- because the binding has since
    // been turned off, say -- swallowing a tap it knows nothing about.
    swiped.current = false;
    if (e.pointerType === "mouse") return;
    // Selection mode owns the horizontal: the row is a checkbox target and a
    // second meaning for the same drag would make a bulk selection unpickable.
    if (selecting) return;
    // With both sides unbound -- which is how this ships -- nothing is measured,
    // nothing is stored and the list behaves exactly as it did before the
    // feature existed.
    if (!swipe.anyBound()) return;
    drag.current = {
      x: e.clientX,
      y: e.clientY,
      width: (e.currentTarget as HTMLElement).getBoundingClientRect().width,
      ...swipe.bindings(),
    };
  };

  const moveSwipe = (e: PointerEvent) => {
    const from = drag.current;
    if (!from) return;
    const next = swipe.track(
      e.clientX - from.x,
      e.clientY - from.y,
      from.width,
      from.left,
      from.right,
    );
    if (next) {
      // A drag that has committed to the horizontal is not a long press, and
      // the pending one has to die here: `maybeCancelHold` only cancels past
      // its own slop, which is the same distance, so the two would otherwise
      // race and a slow swipe would enter selection mode halfway through.
      cancelHold();
      swiped.current = true;
    }
    setGesture(next);
  };

  const endSwipe = () => {
    const done = gesture;
    drag.current = null;
    if (!done) return;
    // Cleared before the action runs, so the row is back in place whatever the
    // action does with it -- an archive removes it from the list, and a row
    // animating a transform on its way out is a row still holding a transform
    // when the next message reuses its place.
    setGesture(null);
    if (done.armed) void swipe.perform(row, done.action);
  };

  const cancelSwipe = () => {
    drag.current = null;
    setGesture(null);
  };

  // `offset` is a distance and always positive -- the direction is a separate
  // field, because "how far" and "which way" are different questions and only
  // this line needs them multiplied together.
  const shift = gesture ? (gesture.dir === "left" ? -gesture.offset : gesture.offset) : 0;

  return (
    <div
      class={`row${unread ? " unread" : ""}${selecting ? " selecting" : ""}${gesture ? " swiping" : ""}`}
      role="listitem"
      data-open={isOpen ? "true" : "false"}
      data-checked={checked ? "true" : "false"}
      data-stale={stale ? "true" : "false"}
      style={
        accountColour
          ? ({ "--row-acct": colourVar(accountColour) } as Record<string, string>)
          : undefined
      }
    >
      {/*
        Behind the row, and only while a drag is being drawn. It is
        `aria-hidden` because it is the visible half of a gesture: the action it
        announces is announced again by the toast the action itself raises, and
        a screen reader driving this list is using TalkBack's own swipe, which
        is navigation and never reaches this code.
      */}
      {gesture && <SwipeBackdrop row={row} gesture={gesture} />}

      <button
        class="row-check"
        role="checkbox"
        aria-checked={checked}
        aria-label={`Select ${describe}`}
        onClick={(e) => {
          // Shift on the tick extends, exactly as it does on the row. Someone
          // who has learned one has learned the other.
          if (e.shiftKey) store.extendSelection(row);
          else store.toggleSelect(row);
        }}
      >
        {checked ? <Icon.Check /> : <span class="box" aria-hidden="true" />}
      </button>

      {/*
        An anchor, not a button, and the `href` is real.

        A row goes somewhere: `#/message/<account>/<id>` is an address this app
        resolves. Spelling it as a link is what gives the browser build
        middle-click to open in a tab, right-click to open in a window, "copy
        link to message", and the same for anything driving the page that is not
        a mouse. None of that is code here; it is what a link already means.

        This is deliberately the browser's answer to the desktop's tab strip.
        The strip is desktop-only because a browser already has tabs and windows
        and its own are better than ours would be -- so the capability mirrors
        and the mechanism does not, which is the one honest kind of divergence.

        `onClick` still preventDefaults for a plain click, because the app opens
        the message in the pane rather than navigating. The `href` is for the
        gestures the app deliberately does not handle.
      */}
      <a
        class="row-main"
        href={nav.fragmentFor({ account: row.accountId, email: row.email.id })}
        aria-current={isOpen}
        style={gesture ? { transform: `translateX(${shift}px)` } : undefined}
        onPointerDown={(e) => {
          beginHold(e);
          beginSwipe(e);
        }}
        onPointerMove={(e) => {
          maybeCancelHold(e);
          moveSwipe(e);
        }}
        onPointerUp={(e) => {
          cancelHold();
          endSwipe();
          // A drag has no business also being a tap, and this one ends on an
          // `<a href>`: without it the release navigates, which in this app
          // opens the message and marks it read -- on top of whatever the swipe
          // just did to it.
          if (swiped.current) e.preventDefault();
        }}
        onPointerCancel={() => {
          cancelHold();
          cancelSwipe();
        }}
        onClick={(e) => {
          // The press already did something, so the tap that ends it must not
          // also open the message -- which on this app means marking it read.
          if (held.current) {
            held.current = false;
            e.preventDefault();
            return;
          }
          // The same, for a swipe. Whether the gesture armed or snapped back,
          // the finger that made it was not asking for this message to open.
          if (swiped.current) {
            swiped.current = false;
            e.preventDefault();
            return;
          }
          // Modifier-click selects instead of opening: the same gesture every
          // file manager and every mail client uses, and the reason a
          // checkbox is not the only way in.
          //
          // It is also why ctrl-click is *not* "open in a new tab" here despite
          // being a link -- that gesture is spoken for, and middle-click is the
          // browser's other idiom for the same thing and is free.
          if (e.shiftKey) {
            e.preventDefault();
            store.extendSelection(row);
            return;
          }
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            store.toggleSelect(row);
            return;
          }
          e.preventDefault();
          // `detail` is the click count. A double-click therefore also fires
          // this once as a single click, which is deliberate rather than
          // tolerated: the message opens in the pane immediately and the second
          // click promotes it. Delaying the first click to tell the two apart
          // would tax the app's primary action to serve its rarer one.
          if (e.detail >= 2) {
            void promote({
              account: row.accountId,
              email: row.email.id,
              subject: row.email.subject ?? undefined,
            });
            return;
          }
          // Not `store.open`. A draft opens in the composer rather than in the
          // reader -- there was previously no way back into one at all, since
          // clicking it showed the read-only view of a message nobody had
          // finished writing. See `activate` in `lib/composeWindow.ts`, and note
          // that the keyboard cursor deliberately does *not* come through here:
          // `j` through a Drafts folder must not raise a composer per row.
          void composeWindow.activate(row);
        }}
      >
        <span class="avatar" style={{ background: `hsl(${hue} 55% 45%)` }} aria-hidden="true">
          {initials(counterpart)}
        </span>
        <span class="row-text">
          <span class="row-top">
            {/*
              "To" in front of a recipient, because the name alone is ambiguous
              in a list that mixes directions -- a search returns both, and
              without it a reply from Alice and a message to Alice are the same
              row. Two characters, so it costs nothing at 375px.
            */}
            {/*
              The space between the label and the name is a real text node and
              not the margin. A screen reader reads the concatenation, and
              margin is not a character: without it the row announces "TOAda
              Okafor". The margin stays for the visual gap the space alone is
              too small to give.
            */}
            <span class="who">
              {outgoing && (
                <>
                  <span class="to-mark">To</span>{" "}
                </>
              )}
              {who}
            </span>
            {flagged && <Icon.Star class="icon flag" />}
            <span class="when" title={when}>
              {shortDate(when)}
            </span>
          </span>
          <div class="subject" onPointerEnter={titleWhenClipped}>
            {email.subject || "(no subject)"}
          </div>
          <Snippet fallback={email.preview} highlighted={snippet?.preview ?? null} />
          {(email.hasAttachment ||
            reminder ||
            stale ||
            lowPriorityButShown ||
            lowPriorityHere ||
            (multiAccount && accountName)) && (
            <span class="row-tags">
              {reminder && (
                <span class={reminder.state === "overdue" ? "tag reminder overdue" : "tag reminder"}>
                  <Icon.Bell class="icon" /> {reminders.describe(reminder)}
                </span>
              )}
              {email.hasAttachment && (
                <span class="tag">
                  <Icon.Paperclip class="icon" /> attachment
                </span>
              )}
              {/*
                Words, not just a dimmed row: the reader needs to know this one
                is being kept for them rather than wonder why it is still here,
                and a screen reader gets nothing at all out of 70% opacity.
              */}
              {stale && (
                <span class="tag stale">
                  <Icon.Filter class="icon" /> no longer matches
                </span>
              )}
              {/*
                A demoted sender's message showing in the focused tab, which
                can only be because it is flagged. Said out loud, because
                otherwise the reader sees a row from somebody they thought they
                had silenced and concludes the rule stopped working -- when in
                fact their own star is what pulled it back.
              */}
              {lowPriorityButShown && (
                <span class="tag">
                  <Icon.Star class="icon" /> low priority, flagged
                </span>
              )}
              {/*
                Words rather than a dimmed row, for the reason `stale` above
                gives: 70% opacity says nothing to a screen reader, and in a
                search the reader is scanning rather than reading, so a shade of
                grey is the first thing to go unnoticed. No icon and no fill --
                see `.tag.quiet` -- because this can be true of most rows in a
                result set and a column of pills would read as an alert.
              */}
              {lowPriorityHere && <span class="tag quiet">low priority</span>}
              {multiAccount && accountName && <span class="tag account">{accountName}</span>}
            </span>
          )}
        </span>
      </a>
    </div>
  );
}

/**
 * The name `lib/swipe.ts` gives an icon, resolved to something that draws.
 *
 * A `Record` over the union, so an action given a new icon there does not
 * compile until it exists here -- the same forcing function `lib/inventory.test.ts`
 * is built out of. The alternative, a `switch` with a default, would have drawn
 * the wrong picture over the right word and nothing would have said so.
 */
const SWIPE_ICONS: Record<swipe.SwipeIcon, ComponentType<{ class?: string }>> = {
  archive: Icon.Archive,
  reply: Icon.Reply,
  star: Icon.Star,
  "mail-open": Icon.MailOpen,
  "mail-closed": Icon.MailClosed,
};

/**
 * What is underneath the row while it is being dragged.
 *
 * The label is the concrete act on *this* message -- "Mark unread" rather than
 * "Read or unread" -- because it is the sentence somebody lets go on the
 * strength of. See `describe` in `lib/swipe.ts`.
 *
 * It sits on the side the row is uncovering, which is the side the finger is
 * moving away from, so the words appear from behind the row rather than being
 * revealed under the thumb that is hiding them.
 */
function SwipeBackdrop({ row, gesture }: { row: Envelope; gesture: swipe.Gesture }) {
  const { label, icon, tone } = swipe.describe(row, gesture.action);
  const Glyph = SWIPE_ICONS[icon];
  return (
    <div
      class={`row-swipe swipe-${gesture.dir} swipe-${tone}`}
      data-armed={gesture.armed ? "true" : "false"}
      aria-hidden="true"
    >
      <Glyph class="icon" />
      <span>{label}</span>
    </div>
  );
}

/**
 * Reveal the full text on hover, but only when the line is actually cut off.
 *
 * A permanent `title` would put a tooltip on every subject in the list,
 * including the short ones already readable in full -- a box that appears over
 * the list to tell the reader what the list says. The subjects that need it are
 * exactly the ones the ellipsis truncated, and that is a measurement, not a
 * property of the string: it depends on the pane width, which is now something
 * the reader drags.
 *
 * So it is measured on entry rather than at render. `scrollWidth > clientWidth`
 * costs one layout read on the single row under the pointer, where the
 * alternative -- a ref and an observer per row -- measures the whole list to
 * answer a question about one of them. The attribute is removed again when it
 * does not apply, because a row that was narrow when it was last hovered must
 * not keep a stale tooltip after the pane grows.
 */
function titleWhenClipped(e: { currentTarget: HTMLElement }) {
  const el = e.currentTarget;
  if (el.scrollWidth > el.clientWidth) el.title = el.textContent ?? "";
  else el.removeAttribute("title");
}

/**
 * A search snippet, with only `<mark>` allowed through.
 *
 * The server already escapes the surrounding text, so this is belt and braces
 * -- but the alternative is `dangerouslySetInnerHTML` on a string built from
 * message content, and "the server escapes it" is not a property this side can
 * check. Escaping again and restoring exactly two tags can only be safe.
 */
function Snippet({ highlighted, fallback }: { highlighted: string | null; fallback: string }) {
  if (!highlighted) return <div class="snippet">{fallback}</div>;
  const html = escapeHtml(highlighted)
    .replaceAll("&lt;mark&gt;", "<mark>")
    .replaceAll("&lt;/mark&gt;", "</mark>");
  return <div class="snippet" dangerouslySetInnerHTML={{ __html: html }} />;
}

function Skeletons() {
  return (
    <div aria-hidden="true">
      {Array.from({ length: 8 }, (_, i) => (
        <div class="skeleton-row" key={i}>
          <div class="sk circle" />
          <div>
            <div class="sk" style={{ width: `${40 + ((i * 13) % 35)}%` }} />
            <div class="sk" style={{ width: `${55 + ((i * 7) % 30)}%`, marginTop: 7 }} />
            <div class="sk" style={{ width: `${30 + ((i * 11) % 45)}%`, marginTop: 7 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * A double-click: put this message in a tab, or in a window of its own.
 *
 * Which one is the person's choice -- see `openInNewWindow` in
 * `lib/settings.ts`. Either way the new view takes focus, because a
 * double-click is a request to read that message rather than to put it
 * somewhere for later.
 *
 * A window that could not be opened leaves the message where it already is:
 * open in the pane, from the first click of the pair. So the failure costs the
 * promotion and never the reading.
 *
 * # In a browser it is a browser tab, and that is the whole design
 *
 * This used to call `tabs.open` unconditionally, so a double-click in the web
 * build grew the desktop's tab strip inside the page -- a second view of a
 * message stacked on top of an app that has no window manager to put it in.
 * The decision, taken when tabs were designed, is that the browser gets the
 * *platform's* affordance instead: the same address, opened as a real tab that
 * the browser already knows how to move, close and restore.
 *
 * `multiWindow` decides, and not `windowRole()`. That was the bug: `windowRole`
 * answers `"main"` on both builds, because it says what this window is rather
 * than what the build can do, so branching on it saw no difference at all.
 */
async function promote(address: tabs.Address): Promise<void> {
  if (!multiWindow) {
    // The `#/read/` address, not the ordinary one: a browser tab opened from a
    // double-click should be the message, the way a detached window is on the
    // desktop -- not a second full copy of the client with that message
    // selected in it. See `soloFragmentFor`.
    //
    // `noopener` because the new page is a separate document and has no
    // business holding a handle to this one.
    window.open(nav.soloFragmentFor(address), "_blank", "noopener");
    return;
  }
  if (!openInNewWindow.value) {
    tabs.open(address);
    return;
  }
  try {
    await openMessageWindow(address);
  } catch (e) {
    store.toast(
      `Could not open that message in a window \u2014 ${e instanceof Error ? e.message : String(e)}`,
      "error",
    );
  }
}
